import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    Alert,
    InputAdornment,
    useMediaQuery,
    useTheme,
    tooltipClasses,
    TooltipProps,
    Chip,
    Snackbar,
} from '@mui/material';
import { Search, Logout, Devices, Smartphone, Computer, Language, DeleteSweep } from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface Session {
    id: number;
    user_id: number;
    user_name: string;
    user_phone: string;
    user_role: string;
    tenant_name: string | null;
    token_type: string;
    ip_address: string | null;
    user_agent: string | null;
    location: string | null;
    login_time: string;
    last_used_at: string | null;
}

const getDeviceInfo = (ua: string | null): { type: string; icon: React.ReactElement; browser: string } => {
    if (!ua) return { type: 'Unknown', icon: <Devices fontSize="small" />, browser: 'Unknown' };
    const isMobile = /mobile|android|iphone|ipad/i.test(ua);
    const browser = /chrome/i.test(ua) ? 'Chrome'
        : /firefox/i.test(ua) ? 'Firefox'
        : /safari/i.test(ua) ? 'Safari'
        : /edge/i.test(ua) ? 'Edge'
        : /okhttp/i.test(ua) ? 'Mobile App'
        : 'Other';
    return {
        type: isMobile ? 'Mobile' : 'Desktop',
        icon: isMobile ? <Smartphone fontSize="small" /> : <Computer fontSize="small" />,
        browser,
    };
};

const timeAgo = (date: string | null): string => {
    if (!date) return 'Never';
    const diff = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

const ActiveSessions = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
    const [sessionToTerminate, setSessionToTerminate] = useState<Session | null>(null);
    const [terminateAllDialogOpen, setTerminateAllDialogOpen] = useState(false);
    const [userToTerminate, setUserToTerminate] = useState<Session | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['active-sessions', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;
            const response = await api.get('/superadmin/active-sessions', { params });
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    const terminateMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/superadmin/active-sessions/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
            setSuccessMsg('Session terminated successfully');
        },
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    const terminateAllMutation = useMutation({
        mutationFn: async (userId: number) => api.post('/superadmin/active-sessions/terminate-all', { user_id: userId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
            setSuccessMsg('All sessions for user terminated');
        },
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    const handleTerminateClick = (session: Session) => {
        setSessionToTerminate(session);
        setTerminateDialogOpen(true);
    };

    const handleTerminateConfirm = () => {
        if (sessionToTerminate) terminateMutation.mutate(sessionToTerminate.id);
        setTerminateDialogOpen(false);
        setSessionToTerminate(null);
    };

    const handleTerminateAllClick = (session: Session) => {
        setUserToTerminate(session);
        setTerminateAllDialogOpen(true);
    };

    const handleTerminateAllConfirm = () => {
        if (userToTerminate) terminateAllMutation.mutate(userToTerminate.user_id);
        setTerminateAllDialogOpen(false);
        setUserToTerminate(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'user_name',
            headerName: 'User',
            flex: 1,
            minWidth: 180,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>{params.row.user_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{params.row.user_phone}</Typography>
                </Box>
            ),
        },
        {
            field: 'user_role',
            headerName: 'Role',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value?.replace('_', ' ')}
                    size="small"
                    color={params.value === 'super_admin' ? 'error' : params.value === 'admin' ? 'primary' : 'default'}
                    sx={{ textTransform: 'capitalize' }}
                />
            ),
        },
        {
            field: 'tenant_name',
            headerName: 'Tenant',
            width: 150,
            renderCell: (params: GridRenderCellParams) => params.value || '—',
        },
        {
            field: 'device',
            headerName: 'Device',
            width: 140,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                const device = getDeviceInfo(params.row.user_agent);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {device.icon}
                        <Typography variant="body2">{device.browser}</Typography>
                    </Box>
                );
            },
        },
        {
            field: 'ip_address',
            headerName: 'IP Address',
            width: 130,
            renderCell: (params: GridRenderCellParams) => params.value || '—',
        },
        {
            field: 'location',
            headerName: 'Location',
            width: 180,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {params.value && <Language fontSize="small" color="action" />}
                    <Typography variant="body2">{params.value || '—'}</Typography>
                </Box>
            ),
        },
        {
            field: 'token_type',
            headerName: 'Type',
            width: 110,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value === 'offline_token' ? 'Offline' : 'Online'}
                    size="small"
                    variant="outlined"
                    color={params.value === 'offline_token' ? 'warning' : 'success'}
                />
            ),
        },
        {
            field: 'login_time',
            headerName: 'Login Time',
            width: 160,
            valueFormatter: (value: string) => value ? new Date(value).toLocaleString() : 'N/A',
        },
        {
            field: 'last_used_at',
            headerName: 'Last Active',
            width: 130,
            renderCell: (params: GridRenderCellParams) => {
                const ago = timeAgo(params.value);
                const isRecent = params.value && (Date.now() - new Date(params.value).getTime()) < 300000; // 5 min
                return (
                    <Chip
                        label={ago}
                        size="small"
                        color={isRecent ? 'success' : 'default'}
                        variant={isRecent ? 'filled' : 'outlined'}
                    />
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 140,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <BootstrapTooltip title="Terminate Session" placement="top">
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ mr: 0.5, textTransform: 'none', minWidth: 'auto', p: '4px', backgroundColor: '#EF4444' }}
                            onClick={() => handleTerminateClick(params.row)}
                        >
                            <Logout fontSize="small" sx={{ color: '#fff' }} />
                        </Button>
                    </BootstrapTooltip>
                    <BootstrapTooltip title="Terminate All User Sessions" placement="top">
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ textTransform: 'none', minWidth: 'auto', p: '4px', backgroundColor: '#7C3AED' }}
                            onClick={() => handleTerminateAllClick(params.row)}
                        >
                            <DeleteSweep fontSize="small" sx={{ color: '#fff' }} />
                        </Button>
                    </BootstrapTooltip>
                </Box>
            ),
        },
    ];

    const mobileColumns: GridColDef[] = [
        {
            field: 'user_name',
            headerName: 'User',
            flex: 1,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>{params.row.user_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {params.row.ip_address || 'No IP'} · {timeAgo(params.row.last_used_at)}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton size="small" color="error" onClick={() => handleTerminateClick(params.row)}>
                    <Logout fontSize="small" />
                </IconButton>
            ),
        },
    ];

    const rows = data?.data?.data || [];
    const totalRows = data?.data?.total || 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">Active Sessions</Typography>
                    <Typography color="text.secondary">
                        Monitor and manage all active login sessions
                    </Typography>
                </Box>
                <Chip
                    icon={<Devices />}
                    label={`${totalRows} Active`}
                    color="primary"
                    variant="outlined"
                />
            </Box>

            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search by name, phone, tenant, IP, or location..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{ width: isMobile ? '100%' : 400 }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                    <DataGrid
                        rows={rows}
                        columns={isMobile ? mobileColumns : columns}
                        rowCount={totalRows}
                        loading={isLoading}
                        pageSizeOptions={[10, 25, 50]}
                        paginationModel={pageModel}
                        paginationMode="server"
                        onPaginationModelChange={setPageModel}
                        disableRowSelectionOnClick
                        getRowHeight={() => 'auto'}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-cell': { py: 1 },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f8f9fa',
                                borderRadius: 1,
                            },
                        }}
                    />
                </CardContent>
            </Card>

            {/* Terminate Single Session Dialog */}
            <Dialog open={terminateDialogOpen} onClose={() => setTerminateDialogOpen(false)}>
                <DialogTitle>Terminate Session</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to terminate the session for <strong>{sessionToTerminate?.user_name}</strong>?
                    </Typography>
                    {sessionToTerminate?.ip_address && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            IP: {sessionToTerminate.ip_address}
                            {sessionToTerminate.location && ` · ${sessionToTerminate.location}`}
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        This will log them out immediately.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTerminateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleTerminateConfirm} variant="contained" color="error">
                        Terminate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Terminate All Sessions Dialog */}
            <Dialog open={terminateAllDialogOpen} onClose={() => setTerminateAllDialogOpen(false)}>
                <DialogTitle>Terminate All Sessions</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to terminate <strong>all sessions</strong> for <strong>{userToTerminate?.user_name}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This will log them out from all devices immediately.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTerminateAllDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleTerminateAllConfirm} variant="contained" color="error">
                        Terminate All
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!successMsg}
                autoHideDuration={3000}
                onClose={() => setSuccessMsg('')}
                message={successMsg}
            />
        </Box>
    );
};

export default ActiveSessions;
